// src/common/modal/EditPatientModal.jsx
import { useState, useEffect } from "react";
import { Modal, Button, FormField, Dropdown, MutationError } from "@common";
import { LEAD_CATEGORIES } from "@utils/contants";
import { useUpdatePatient } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { toast } from "react-hot-toast";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { FORM_IDS } from "@utils";

export default function EditPatientModal({ isOpen, onClose, patient }) {
  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.EDIT_PATIENT_MODAL,
  );

  const [patientName, setPatientName] = useState("");
  const [leadCategory, setLeadCategory] = useState(LEAD_CATEGORIES[0]);
  const [otherText, setOtherText] = useState("");
  const [referringDoctor, setReferringDoctor] = useState("");
  const [date, setDate] = useState("");

  const updatePatientMutation = useUpdatePatient();

  // Populate form when patient changes
  useEffect(() => {
    if (patient) {
      setPatientName(patient.name || "");
      const currentLead = patient.lead ?? patient.leadCategory;
      if (currentLead && currentLead.startsWith("Other: ")) {
        setLeadCategory("Other");
        setOtherText(currentLead.replace("Other: ", ""));
        setReferringDoctor("");
      } else if (currentLead && currentLead.startsWith("Doctor/GDP Referral: ")) {
        setLeadCategory("Doctor/GDP Referral");
        setReferringDoctor(currentLead.replace("Doctor/GDP Referral: ", ""));
        setOtherText("");
      } else {
        const validLead =
          currentLead && currentLead !== "-" && LEAD_CATEGORIES.includes(currentLead)
            ? currentLead
            : (LEAD_CATEGORIES?.[0] ?? "");
        setLeadCategory(validLead);
        setOtherText("");
        setReferringDoctor("");
      }
      setDate(patient.date || "");
    }
  }, [patient]);

  const isOther = leadCategory === "Other";
  const isDoctorReferral = leadCategory === "Doctor/GDP Referral";
  const canSubmit =
    patientName.trim().length > 0 &&
    patient &&
    (!isOther || otherText.trim().length > 0) &&
    (!isDoctorReferral || referringDoctor.trim().length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const patientData = {
        name: patientName.trim(),
        lead: isOther
          ? `Other: ${otherText.trim()}`
          : isDoctorReferral
            ? `Doctor/GDP Referral: ${referringDoctor.trim()}`
            : leadCategory,
        date: date,
      };

      await updatePatientMutation.mutateAsync({
        id: patient.id,
        data: patientData,
      });

      recordSubmitSuccess();

      toast.success("Patient updated successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to update patient:", error);
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleClose = () => {
    if (!updatePatientMutation.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Patient"
      size="sm"
      footer={
        <>
          <Button
            as="button"
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={updatePatientMutation.isPending}
            className="rounded-full px-6 py-2 border-2 border-palette-gray-border text-palette-gray-heading hover:border-palette-gray-placeholder"
          >
            Cancel
          </Button>

          <Button
            as="button"
            type="submit"
            form="edit-patient-form"
            disabled={!canSubmit || updatePatientMutation.isPending}
            className="rounded-full px-6 py-2 font-semibold text-white
                       bg-gradient-to-r from-palette-indigo-ring to-palette-indigo-label
                       hover:from-palette-indigo-label hover:to-palette-indigo-badge
                       disabled:opacity-40"
          >
            {updatePatientMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </>
      }
    >
      <form
        id="edit-patient-form"
        onSubmit={handleSubmit}
        className="space-y-4"
        {...formTrackingProps}
      >
        <FormField
          label="Patient Name"
          placeholder="Patient name"
          value={patientName}
          onChange={(e) => setPatientName(e.target.value)}
          required
        />

        <Dropdown
          label="Lead Category"
          value={leadCategory}
          onChange={(value) => setLeadCategory(value)}
          options={LEAD_CATEGORIES}
        />

        {isDoctorReferral && (
          <FormField
            label="Referring Doctor Name"
            placeholder="Enter referring doctor's name"
            value={referringDoctor}
            onChange={(e) => setReferringDoctor(e.target.value)}
            required
          />
        )}

        {isOther && (
          <FormField
            label="Please specify"
            placeholder="Enter lead category"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            required
          />
        )}

        <FormField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <MutationError
          show={updatePatientMutation.isError}
          error={updatePatientMutation.error}
          fallbackMessage="Failed to update patient"
          className="mt-3"
        />
      </form>
    </Modal>
  );
}
